import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Bookmark, Camera, CarFront } from 'lucide-react'

import Header from '../../components/Header'
import Footer from '../../components/Footer'
import PrivateTourEnquiryModal from '../../components/PrivateTourEnquiryModal'
import useSavedPrivateTours from '../../hooks/useSavedPrivateTours'
import LocationDropdown from '../../components/LocationDropdown'
import DateTimePicker, {
  type DateTimeValue,
  formatDateLabel,
  formatTimeLabel,
} from '../../components/DateTimePicker'
import { bookingService } from '../../api/services/bookingService'
import type { Vehicle } from '../../types/booking'

import {
  apiGetPrivateTours,
  apiGetPrivateTourById,
  type PrivateTour,
} from '../../api/privateTours/privateTourApi'

export default function PrivateTourListing() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const tourId = searchParams.get('id')
  const [activeInfoTab, setActiveInfoTab] = useState<
    'overview' | 'itinerary'
  >('overview')
  const [tours, setTours] = useState<PrivateTour[]>([])
  const [selectedTour, setSelectedTour] =
    useState<PrivateTour | null>(null)

  // Fetch all tours for listing
  useEffect(() => {
    async function loadTours() {
      const res = await apiGetPrivateTours()

      if (res.success && res.data) {
        const activeTours = res.data.filter((tour) => tour.active === true)
        setTours(activeTours)
      }
    }

    loadTours()
  }, [])

  // Fetch details of selected tour
  useEffect(() => {
    const selectedId = tourId

    if (selectedId) {
      async function loadDetail(id: string) {
        const res = await apiGetPrivateTourById(id)

        if (res.success && res.data && res.data.active === true) {
          setSelectedTour(res.data)
        } else {
          setSelectedTour(null)
        }
      }

      loadDetail(selectedId)
    } else {
      setSelectedTour(null)
    }
  }, [tourId])

  const setSelectedTourId = (id: string | null) => {
    if (id) {
      setSearchParams({ id })
    } else {
      setSearchParams({})
    }
  }

  const [searchInput, setSearchInput] = useState('')

  const {
    savedIds: savedTourIds,
    toggleSaved: toggleSavedTour,
  } = useSavedPrivateTours()

  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [selectedTour])

  const tripHighlights = selectedTour?.highlights || [
    'Private Mercedes Van and professional driver',
    'Customizable routes and photo stops',
    'Scenic cross-border travel experiences',
    'Bottled water and Wi-Fi on board',
  ]

  const excludedFeatures =
    (selectedTour?.excludedFeatures && selectedTour.excludedFeatures.length > 0)
      ? selectedTour.excludedFeatures
      : (selectedTour?.excluded_features && selectedTour.excluded_features.length > 0)
        ? selectedTour.excluded_features
        : [
            'Food & drinks',
            'Entry tickets & museum admissions',
            'Hotel accommodation',
            'Gratuities / tips',
          ]

  const expectations = [
    {
      title: 'Premium Door-to-Door Service',
      desc: 'Get picked up directly from your hotel or preferred address in Salzburg. Sit back and travel across borders in absolute comfort.',
    },
    {
      title: 'Expert Driver-Guides',
      desc: 'Our driver-guides know the local roads, history, and hidden gems. Learn about European culture as you travel in style.',
    },
    {
      title: 'Complete Flexibility',
      desc: 'Want to stop for a photo, explore a village, or grab lunch at a local tavern? Your private itinerary is fully flexible.',
    },
  ]

  const goodToKnowList = selectedTour?.goodToKnow || [
    'Free cancellation up to 24 hours before departure.',
    'Child booster seats available for free.',
    'Cross-border toll fees are included in the price.',
    'Suitable for groups, corporate travel, and families.',
  ]

  const normalizedSearchQuery =
    searchInput.trim().toLowerCase()

  const searchTerms = normalizedSearchQuery
    .split(/\s+/)
    .filter(Boolean)

  const filteredTours =
    searchTerms.length === 0
      ? tours
      : tours.filter((tour) => {
          const searchableContent = [
            tour.title,
            tour.subtitle,
            tour.shortDescription,
            tour.routeQuery,
            tour.duration,
            tour.price,
            ...(tour.categories?.length ? tour.categories : tour.tags),
            ...(tour.highlights || []),
          ]
            .join(' ')
            .toLowerCase()

          return searchTerms.every((term) =>
            searchableContent.includes(term)
          )
        })

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {selectedTour ? (
        /* =========================================================
           DETAIL VIEW
        ========================================================= */
        <div className="flex flex-1 flex-col">
          <div className="relative z-20 border-b border-slate-100 bg-white px-4">
            <Header />
          </div>

          <main className="mx-auto w-full max-w-[1280px] px-4 py-8 sm:px-6 sm:pb-12 sm:pt-4 lg:px-[42px]">
            {/* Back Button + Title */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <button
                onClick={() => setSelectedTourId(null)}
                className="flex items-center gap-1.5 self-start rounded-full border border-[#E5E5E5] bg-white px-4 py-2 font-host text-sm font-semibold text-[#333333] transition-all hover:bg-slate-50 active:scale-[0.98] sm:self-auto"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="text-[#333333]"
                >
                  <path
                    d="M10 13L5 8L10 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span>Back</span>
              </button>

              <h1 className="font-host text-[22px] font-bold text-[#111111] sm:text-[28px]">
                {selectedTour.title}
              </h1>
            </div>

            {/* Photo Gallery */}
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="h-[240px] overflow-hidden rounded-[7px] shadow-[0_2px_8px_rgba(0,0,0,0.04)] sm:h-[280px] lg:h-[320px]">
                <img
                  src={
                    selectedTour.images?.[0] ||
                    selectedTour.image ||
                    '/assets/images/van-tour-1.png'
                  }
                  alt={selectedTour.title}
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.01]"
                />
              </div>

              <div className="grid h-[240px] grid-cols-2 gap-4 sm:h-[280px] lg:h-[320px]">
                {(
                  selectedTour.images?.length
                    ? [
                        selectedTour.images[1] ||
                          selectedTour.images[0],
                        selectedTour.images[2] ||
                          selectedTour.images[0],
                        selectedTour.images[3] ||
                          selectedTour.images[0],
                        selectedTour.images[4] ||
                          selectedTour.images[0] ||
                          selectedTour.image,
                      ]
                    : [
                        '/assets/images/van-tour-2.png',
                        '/assets/images/van-tour-3.png',
                        '/assets/images/van-tour-4.png',
                        '/assets/images/van-tour-1.png',
                      ]
                ).map((imgSrc, idx) => (
                  <div
                    key={idx}
                    className="overflow-hidden rounded-[7px] shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                  >
                    <img
                      src={imgSrc}
                      alt=""
                      className="h-full w-full object-cover transition-all duration-300 hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Subtitle and enquiry button */}
            <div className="mt-6 flex items-center justify-between gap-4">
              <h2 className="min-w-0 break-words font-host text-[20px] font-bold text-[#111111] sm:text-[24px]">
                {selectedTour.subtitle}
              </h2>

              <button
                type="button"
                onClick={() => setIsEnquiryOpen(true)}
                className="shrink-0 whitespace-nowrap rounded-full bg-[#FF6A1A] px-5 py-3 font-host text-[13px] font-bold text-white shadow-[0_4px_12px_rgba(255,106,26,0.15)] transition-all hover:bg-[#E05B13] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6A1A] focus-visible:ring-offset-2 sm:px-8 sm:text-[15px]"
              >
                Enquire Now
              </button>
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-6 border-b border-slate-100 py-5 text-[14px] font-medium text-[#4A4A4A]">
              <div className="flex items-center gap-2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-brand-orange"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>

                <span className="font-host text-[14px] font-semibold text-[#444444]">
                  {selectedTour.duration}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-brand-orange"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>

                <span className="font-host text-[14px] font-semibold text-[#444444]">
                  Max People: {selectedTour.maxPeople ?? 8}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-brand-orange"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>

                <span className="font-host text-[14px] font-semibold text-[#444444]">
                  Start Location:{' '}
                  {selectedTour.startLocation ??
                    selectedTour.routeQuery ??
                    'Salzburg'}
                </span>
              </div>
            </div>

            {/* Split Layout */}
            <div className="mt-10">
              {/* LEFT */}
              <div>
                {/* Tour Overview */}
                <section>
                  {/* Tabs */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveInfoTab('overview')}
                      className={`rounded-full border px-4 py-1.5 font-host text-[12px] font-bold transition-all ${
                        activeInfoTab === 'overview'
                          ? 'border-slate-200 bg-slate-100 text-slate-800'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Tour Overview
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveInfoTab('itinerary')}
                      className={`rounded-full border px-4 py-1.5 font-host text-[12px] font-bold transition-all ${
                        activeInfoTab === 'itinerary'
                          ? 'border-brand-green bg-slate-100 text-slate-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Itinerary
                    </button>
                  </div>

                  {/* Content */}
                  {activeInfoTab === 'overview' ? (
                    <p className="mt-4 font-host text-base leading-[1.65] text-[#4A4A4A] whitespace-pre-line">
                      {selectedTour.overview ||
                        selectedTour.shortDescription}
                    </p>
                  ) : (
                    <div className="mt-4 rounded-[12px] border border-slate-100 bg-[#F8FAFC] px-5 py-5 sm:px-6">
                      <h3 className="font-host text-[17px] font-bold text-[#111111]">
                        Itinerary
                      </h3>

                      <p className="mt-2 font-host text-[14px] sm:text-[15px] leading-[1.7] text-[#4A4A4A] whitespace-pre-line">
                        {selectedTour.itinerary ||
                          'No itinerary available.'}
                      </p>
                    </div>
                  )}
                </section>

                {/* Highlights */}
                <section className="mt-10">
                  <h3 className="font-host text-[18px] font-bold text-[#111111]">
                    Trip Highlights
                  </h3>

                  <ul className="mt-4 space-y-3.5">
                    {tripHighlights.map((hl, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3"
                      >
                        <div className="h-2 w-2 rounded-full bg-[#FF7A29]" />

                        <span className="font-host text-[14px] font-semibold text-[#3C3C3C] sm:text-[15px]">
                          {hl}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>

                {/* Excluded Features */}
                {excludedFeatures.length > 0 && (
                  <section className="mt-10">
                    <h3 className="font-host text-[18px] font-bold text-[#111111]">
                      Excluded Features
                    </h3>

                    <ul className="mt-4 space-y-3.5">
                      {excludedFeatures.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-3"
                        >
                          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </div>

                          <span className="font-host text-[14px] font-semibold text-[#555555] sm:text-[15px]">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* What to Expect */}
                <section className="mt-10">
                  <span className="mb-4 inline-block rounded-full border border-slate-200 bg-slate-100 px-3.5 py-1 font-host text-[12px] font-bold text-slate-800">
                    What to Expect
                  </span>

                  <div className="space-y-6">
                    {expectations.map((exp, idx) => (
                      <div
                        key={idx}
                        className="flex gap-4"
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-[13px] font-semibold text-brand-orange">
                          {idx + 1}
                        </div>

                        <div>
                          <h4 className="font-host text-[15px] font-bold text-[#111111] sm:text-[16px]">
                            {exp.title}
                          </h4>

                          <p className="mt-1.5 font-host text-[13px] leading-[1.5] text-[#555555] sm:text-[14px]">
                            {exp.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Good to Know */}
                <section className="mt-10">
                  <span className="mb-4 inline-block rounded-full border border-slate-200 bg-slate-100 px-3.5 py-1 font-host text-[12px] font-bold text-slate-800">
                    Good to Know
                  </span>

                  <ul className="space-y-3.5">
                    {goodToKnowList.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3"
                      >
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M13.3 4.3L6 11.6L2.7 8.3"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>

                        <span className="font-host text-[14px] font-semibold text-[#3C3C3C]">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>

                {/* Map */}
                <section className="mt-10">
                  <div className="h-[360px] w-full overflow-hidden rounded-[24px] border border-slate-200 shadow-sm">
                    <iframe
                      title={`Route Map to ${selectedTour.routeQuery}`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(
                        selectedTour.routeQuery
                      )}&t=&z=9&ie=UTF8&iwloc=&output=embed`}
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                </section>
              </div>

              {/* ===================================================
                  SUMMARY CARD
              =================================================== */}
              {false && (
              <div>
                <div className="sticky top-6 flex flex-col gap-6">
                  <div className="rounded-[28px] border border-[#E5E5E5] bg-white p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="font-host text-[26px] font-bold leading-none text-[#111111]">
                            From €{selectedTour?.price ?? 480}
                          </span>
                        </div>

                        <div className="mt-1 flex items-center gap-1 font-host text-[12px] font-medium text-slate-500">
                          <span>per vehicle</span>

                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            className="text-slate-400"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                            />
                            <line
                              x1="12"
                              y1="16"
                              x2="12"
                              y2="12"
                            />
                            <line
                              x1="12"
                              y1="8"
                              x2="12.01"
                              y2="8"
                            />
                          </svg>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 rounded-full bg-[#3b629b] px-4 py-2 text-[12px] font-semibold text-white">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <rect
                            x="3"
                            y="11"
                            width="18"
                            height="9"
                            rx="2"
                          />
                          <path d="M5 11V6a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v5" />
                          <circle
                            cx="7"
                            cy="15"
                            r="1.5"
                            fill="currentColor"
                          />
                          <circle
                            cx="17"
                            cy="15"
                            r="1.5"
                            fill="currentColor"
                          />
                        </svg>

                        <span>Private Tour</span>
                      </div>
                    </div>

                    {/* Cancellation */}
                    <div className="mt-6 flex items-start gap-2.5 rounded-[12px] border border-[#CCE5CC] bg-[#F7FDF7] p-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="mt-0.5 shrink-0 text-green-700"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>

                      <span className="font-host text-[13px] font-semibold leading-normal text-green-800">
                        Free cancellation up to 24 hours
                        before your departure time.
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsEnquiryOpen(true)}
                      className="mt-6 w-full rounded-full bg-[#FF6A1A] py-4 font-host text-[16px] font-bold text-white shadow-[0_4px_12px_rgba(255,106,26,0.15)] transition-all hover:bg-[#E05B13] active:scale-[0.98]"
                    >
                      Enquire Now
                    </button>
                  </div>
                </div>
              </div>
             )}
            </div>
          </main>
        </div>
      ) : (
        /* =========================================================
           LISTING VIEW
        ========================================================= */
        <div className="flex flex-1 flex-col bg-white">
          {/* Hero */}
          <section className="relative flex h-[420px] items-center overflow-hidden bg-slate-50 sm:h-[500px] lg:h-[540px]">
            <img
              src="/assets/images/hero-bg-main.png"
              alt=""
              className="absolute inset-0 z-0 h-full w-full object-cover"
            />

            <div className="absolute inset-0 z-[5] bg-black/10" />

            <div className="absolute left-0 right-0 top-0 z-20 px-4">
              <Header />
            </div>
          </section>

          {/* Listing */}
          <main className="mx-auto flex w-full max-w-[1560px] flex-1 flex-col items-center px-4 py-12 sm:px-6 lg:px-[32px]">
            {/* Title & Subtitle */}
            <div className="flex max-w-[800px] flex-col items-center text-center">
              <h2 className="font-host text-[28px] font-bold leading-tight text-brand-orange sm:text-[36px]">
                Mini Van Tours Across Europe
              </h2>

              <p className="mt-3 max-w-[640px] font-host text-[15px] font-medium leading-relaxed text-slate-700 sm:text-[16px]">
                Travel across Europe with a private driver,
                premium vehicle, and personalized itineraries
                designed around your journey.
              </p>
            </div>

            {/* Search */}
            <div className="relative mt-8 w-full max-w-[620px]">
              <svg
                className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>

              <input
                type="search"
                placeholder="Search destination, route or tour"
                value={searchInput}
                onChange={(event) =>
                  setSearchInput(event.target.value)
                }
                className="w-full rounded-full border border-slate-200 bg-white py-4 pl-14 pr-12 font-host text-base text-slate-800 outline-none shadow-[0_3px_12px_rgba(0,0,0,0.03)] transition-all placeholder:text-slate-400 focus:border-[#365E92] focus:ring-2 focus:ring-[#365E92]/20"
              />

              {searchInput && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchInput('')}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-lg text-slate-400 transition-colors hover:text-slate-700"
                >
                  ×
                </button>
              )}
            </div>

            {/* =====================================================
                TOURS GRID

                MOBILE:
                Card = full available width.

                SM/TABLET/DESKTOP:
                Original 304px card size.
            ===================================================== */}
            <div className="mt-12 grid w-full grid-cols-1 gap-8 sm:grid-cols-2 sm:justify-items-center lg:grid-cols-4">
              {/* No Results */}
              {filteredTours.length === 0 && (
                <div className="col-span-full flex w-full flex-col items-center justify-center py-16 text-center">
                  <div className="grid h-14 w-14 place-items-center rounded-full bg-slate-100 text-slate-400">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle
                        cx="11"
                        cy="11"
                        r="7"
                      />
                      <path d="M20 20l-4-4" />
                    </svg>
                  </div>

                  <h3 className="mt-4 font-host text-lg font-semibold text-slate-800">
                    No private tours found
                  </h3>

                  <p className="mt-1 font-host text-sm text-slate-500">
                    Try searching with another destination or
                    route.
                  </p>

                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="mt-5 rounded-full bg-[#365E92] px-6 py-2.5 font-host text-sm font-semibold text-white transition hover:bg-[#294e7e]"
                  >
                    Clear Search
                  </button>
                </div>
              )}

              {filteredTours.map((tour) => (
                <article
                  key={tour.id}
                  onClick={() =>
                    setSelectedTourId(tour.id)
                  }
                  className="
                    relative
                    flex
                    h-[469px]
                    w-full
                    min-w-0
                    flex-col
                    overflow-hidden
                    rounded-[14px]
                    bg-[#efeeee]
                    shadow-[0_2px_7px_rgba(103,89,89,0.25)]
                    cursor-pointer
                    transition-transform
                    duration-300
                    hover:-translate-y-1
                    hover:shadow-lg

                    sm:w-[304px]
                    sm:min-w-[304px]
                    sm:shrink-0
                  "
                >
                  {/* Duration Badge */}
                  <div className="absolute left-[16px] top-[12px] z-10">
                    <span className="font-host text-[15px] font-semibold leading-none tracking-[-0.01em] text-[#c56c3d]">
                      {tour.duration}
                    </span>
                  </div>

                  {/* =============================================
                      MOBILE IMAGE
                      Responsive clip-path
                  ============================================= */}
                  <div
                    className="relative h-[320px] shrink-0 overflow-hidden sm:hidden"
                    style={{
                      clipPath:
                        'polygon(0% 100%, 0% 17%, 1% 15%, 5% 12.5%, 42% 12.5%, 45% 11%, 47.5% 8%, 49% 3.5%, 52.5% 0%, 95% 0%, 98% 1%, 100% 5%, 100% 100%)',
                    }}
                  >
                    <img
                      src={tour.image}
                      alt={tour.title}
                      className="absolute inset-0 h-full w-full object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />

                    {/* Bookmark */}
                    <button
                      type="button"
                      aria-label={
                        savedTourIds.includes(
                          tour.id.toString()
                        )
                          ? 'Remove saved tour'
                          : 'Save tour'
                      }
                      aria-pressed={savedTourIds.includes(
                        tour.id.toString()
                      )}
                      onClick={(event) => {
                        event.stopPropagation()

                        toggleSavedTour({
                          id: tour.id,
                          title: tour.title,
                          duration: tour.duration,
                          price: tour.price,
                          image: tour.image,
                        })
                      }}
                      className="absolute right-2.5 top-2.5 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/95 shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-transform duration-200 hover:scale-105 active:scale-95"
                    >
                      <Bookmark
                        className={`h-[15px] w-[15px] stroke-[2.1] transition-colors duration-200 ${
                          savedTourIds.includes(
                            tour.id.toString()
                          )
                            ? 'fill-[#EF4444] stroke-[#EF4444] text-[#EF4444]'
                            : 'fill-[#8b8b8b] stroke-[#8b8b8b] text-[#8b8b8b]'
                        }`}
                      />
                    </button>
                  </div>

                  {/* =============================================
                      TABLET + DESKTOP IMAGE
                      ORIGINAL CLIP-PATH
                  ============================================= */}
                  <div
                    className="relative hidden h-[320px] shrink-0 overflow-hidden sm:block"
                    style={{
                      clipPath: `polygon(304px 454px, 304px 454px, 303.803668px 456.433012px, 303.235264px 458.741056px, 302.325676px 460.893244px, 301.105792px 462.858688px, 299.6065px 464.6065px, 297.858688px 466.105792px, 295.893244px 467.325676px, 293.741056px 468.235264px, 291.433012px 468.803668px, 289px 469px, 15px 469px, 15px 469px, 12.56692239px 468.803668px, 10.25884032px 468.235264px, 8.10663693px 467.325676px, 6.14119536px 466.105792px, 4.39339875px 464.6065px, 2.89413024px 462.858688px, 1.67427297px 460.893244px, 0.76471008px 458.741056px, 0.19632471px 456.433012px, 2.4833328970406E-31px 454px, 0px 55px, 0px 55px, 0.19632471px 52.5669151px, 0.76471008px 50.2588288px, 1.67427297px 48.1066237px, 2.89413024px 46.1411824px, 4.39339875px 44.3933875px, 6.14119536px 42.8941216px, 8.10663693px 41.6742673px, 10.25884032px 40.7647072px, 12.56692239px 40.1963239px, 15px 40px, 129px 40px, 129px 40px, 131.433012px 39.8036761px, 133.741056px 39.2352928px, 135.893244px 38.3257327px, 137.858688px 37.1058784px, 139.6065px 35.6066125px, 141.105792px 33.8588176px, 142.325676px 31.8933763px, 143.235264px 29.7411712px, 143.803668px 27.4330849px, 144px 25px, 144px 15px, 144px 15px, 144.196332px 12.56692239px, 144.764736px 10.25884032px, 145.674324px 8.10663693px, 146.894208px 6.14119536px, 148.3935px 4.39339875px, 150.141312px 2.89413024px, 152.106756px 1.67427297px, 154.258944px 0.76471008px, 156.566988px 0.19632471px, 159px 2.4833328970406E-31px, 289px 0px, 289px 0px, 291.433012px 0.19632471px, 293.741056px 0.76471008px, 295.893244px 1.67427297px, 297.858688px 2.89413024px, 299.6065px 4.39339875px, 301.105792px 6.14119536px, 302.325676px 8.10663693px, 303.235264px 10.25884032px, 303.803668px 12.56692239px, 304px 15px, 304px 454px)`,
                    }}
                  >
                    <img
                      src={tour.image}
                      alt={tour.title}
                      className="absolute inset-0 h-full w-full object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />

                    {/* Bookmark */}
                    <button
                      type="button"
                      aria-label={
                        savedTourIds.includes(
                          tour.id.toString()
                        )
                          ? 'Remove saved tour'
                          : 'Save tour'
                      }
                      aria-pressed={savedTourIds.includes(
                        tour.id.toString()
                      )}
                      onClick={(event) => {
                        event.stopPropagation()

                        toggleSavedTour({
                          id: tour.id,
                          title: tour.title,
                          duration: tour.duration,
                          price: tour.price,
                          image: tour.image,
                        })
                      }}
                      className="absolute right-2.5 top-2.5 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/95 shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-transform duration-200 hover:scale-105 active:scale-95"
                    >
                      <Bookmark
                        className={`h-[15px] w-[15px] stroke-[2.1] transition-colors duration-200 ${
                          savedTourIds.includes(
                            tour.id.toString()
                          )
                            ? 'fill-[#EF4444] stroke-[#EF4444] text-[#EF4444]'
                            : 'fill-[#8b8b8b] stroke-[#8b8b8b] text-[#8b8b8b]'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="flex min-h-0 flex-1 flex-col bg-white px-[12px] pb-[14px] pt-[10px]">
                    <div>
                      {/* Tags */}
                      <div className="mb-2.5 flex items-center gap-2 text-[12px] leading-none text-[#202020]">
                        {(tour.categories?.length ? tour.categories : tour.tags).map(
                          (category, index) => (
                            <div
                              key={`${tour.id}-${category}`}
                              className="flex items-center gap-2"
                            >
                              <span className="inline-flex items-center gap-1">
                                {index === 1 ? (
                                  <CarFront className="h-3 w-3 fill-current stroke-[2.1]" />
                                ) : (
                                  <Camera className="h-3 w-3 fill-current stroke-[2.1]" />
                                )}
                                {category}
                              </span>

                              {index <
                                (tour.categories?.length
                                  ? tour.categories.length
                                  : tour.tags.length) -
                                  1 && (
                                <span className="text-[#c26f40]">•</span>
                              )}
                            </div>
                          )
                        )}
                      </div>

                      <h3 className="font-host text-[15px] font-semibold leading-[1.23] tracking-[-0.02em] text-[#111111]">
                        {tour.title}
                      </h3>

                      {tour.subtitle && (
                        <p className="mt-1 line-clamp-2 font-host text-[13px] font-medium leading-snug text-slate-500">
                          {tour.subtitle}
                        </p>
                      )}
                    </div>

                    <div className="mt-auto flex items-end justify-between gap-3 pt-3">
                      <div>
                        <p className="font-host text-[12px] leading-none text-[#666666]">
                          Starting From
                        </p>

                        <div className="mt-1 flex items-end gap-1">
                          <span className="font-host text-[19px] font-medium leading-none tracking-[-0.03em] text-[#555555]">
                            €{tour.price}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          setSelectedTourId(tour.id)
                        }}
                        className="h-[38px] shrink-0 rounded-[12px] bg-[#ff7a29] px-[24px] font-host text-[13px] font-semibold tracking-[-0.01em] text-white shadow-[0_2px_6px_rgba(255,122,41,0.28)]"
                      >
                        Explore Trip
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </main>
        </div>
      )}

      {selectedTour && (
        <PrivateTourEnquiryModal
          isOpen={isEnquiryOpen}
          onClose={() => setIsEnquiryOpen(false)}
          tourId={selectedTour.id}
          tourTitle={selectedTour.title}
          estimatedPrice={selectedTour.price ?? 480}
        />
      )}

      <Footer />
    </div>
  )
}
